import { BookOpenText, Info, Layers3, Type } from "lucide-react";

import {
  countWords,
  STRUCTURED_ABSTRACT_FIELDS,
  structuredAbstractToText,
  type Manuscript,
  type ManuscriptAffiliation,
  type ManuscriptAuthor,
  type ManuscriptReference,
  type ManuscriptSection,
  type ManuscriptTable,
  type ManuscriptFigure,
  type StructuredAbstractKey,
} from "../domain/manuscript";
import type { ManuscriptTemplate } from "../domain/templates";
import type { JournalProfileMatch } from "../domain/journal-profiles";
import { AuthorsEditor } from "./AuthorsEditor";
import { FormatRequirementsEditor } from "./FormatRequirementsEditor";
import { JournalProfileMatcher } from "./JournalProfileMatcher";
import type { ActivePanel } from "./OutlinePanel";
import { ReferencesEditor } from "./ReferencesEditor";
import { FiguresEditor } from "./FiguresEditor";
import { TablesEditor } from "./TablesEditor";

type EditorPanelProps = {
  manuscript: Manuscript;
  template: ManuscriptTemplate;
  journalMatch: JournalProfileMatch;
  activeId: ActivePanel;
  onMetadataChange: (
    field: keyof Manuscript["metadata"],
    value: string,
  ) => void;
  onAbstractChange: (
    field: "mode" | "content" | "keywords",
    value: string,
  ) => void;
  onStructuredAbstractChange: (
    field: StructuredAbstractKey,
    value: string,
  ) => void;
  onSectionChange: (
    id: string,
    patch: Partial<Pick<ManuscriptSection, "title" | "content">>,
  ) => void;
  onAddChildSection: (parentId: string) => void;
  onAddAuthor: () => void;
  onAuthorChange: (
    id: string,
    patch: Partial<Omit<ManuscriptAuthor, "id">>,
  ) => void;
  onMoveAuthor: (id: string, direction: -1 | 1) => void;
  onCoFirstChange: (id: string, checked: boolean) => void;
  onCoCorrespondingChange: (id: string, checked: boolean) => void;
  onDeleteAuthor: (id: string) => void;
  onAddAffiliation: () => void;
  onAffiliationChange: (
    id: string,
    patch: Partial<Omit<ManuscriptAffiliation, "id">>,
  ) => void;
  onDeleteAffiliation: (id: string) => void;
  onAddReference: () => void;
  onBulkAddReferences: (items: string[]) => void;
  onReferenceChange: (
    id: string,
    patch: Partial<Omit<ManuscriptReference, "id">>,
  ) => void;
  onMoveReference: (id: string, direction: -1 | 1) => void;
  onDeleteReference: (id: string) => void;
  onAddTable: (rows: string[][]) => void;
  onTableChange: (
    id: string,
    patch: Partial<Omit<ManuscriptTable, "id">>,
  ) => void;
  onMoveTable: (id: string, direction: -1 | 1) => void;
  onDeleteTable: (id: string) => void;
  onAddFigure: () => void;
  onFigureChange: (
    id: string,
    patch: Partial<Omit<ManuscriptFigure, "id">>,
  ) => void;
  onMoveFigure: (id: string, direction: -1 | 1) => void;
  onDeleteFigure: (id: string) => void;
  onTemplateChange: (template: ManuscriptTemplate) => void;
  onOpenTemplateDesigner: () => void;
};

function FieldLabel({
  children,
  optional,
}: {
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <span className="field-label">
      {children}
      {optional ? <small>可选</small> : null}
    </span>
  );
}

export function EditorPanel({
  manuscript,
  template,
  journalMatch,
  activeId,
  onMetadataChange,
  onAbstractChange,
  onStructuredAbstractChange,
  onSectionChange,
  onAddChildSection,
  onAddAuthor,
  onAuthorChange,
  onMoveAuthor,
  onCoFirstChange,
  onCoCorrespondingChange,
  onDeleteAuthor,
  onAddAffiliation,
  onAffiliationChange,
  onDeleteAffiliation,
  onAddReference,
  onBulkAddReferences,
  onReferenceChange,
  onMoveReference,
  onDeleteReference,
  onAddTable,
  onTableChange,
  onMoveTable,
  onDeleteTable,
  onAddFigure,
  onFigureChange,
  onMoveFigure,
  onDeleteFigure,
  onTemplateChange,
  onOpenTemplateDesigner,
}: EditorPanelProps) {
  const section = manuscript.sections.find((item) => item.id === activeId);

  if (activeId === "metadata") {
    return (
      <main className="editor-panel">
        <div className="editor-hero">
          <div className="hero-icon">
            <BookOpenText size={22} />
          </div>
          <div>
            <span className="eyebrow">TITLE PAGE</span>
            <h1>题名信息</h1>
            <p>投稿路线、目标期刊和文章类型已独立放在页面最上方。</p>
          </div>
        </div>

        <section className="editor-card">
          <div className="form-grid single">
            <label>
              <FieldLabel>英文题名</FieldLabel>
              <textarea
                className="title-input"
                onChange={(event) =>
                  onMetadataChange("title", event.target.value)
                }
                placeholder="Paste the exact manuscript title here"
                rows={3}
                value={manuscript.metadata.title}
              />
            </label>
          </div>
          <div className="form-grid single">
            <label>
              <FieldLabel optional>Running title</FieldLabel>
              <input
                onChange={(event) =>
                  onMetadataChange("runningTitle", event.target.value)
                }
                placeholder="Short title"
                value={manuscript.metadata.runningTitle}
              />
            </label>
          </div>
          {manuscript.submissionRoute === "journal" ? (
            <JournalProfileMatcher
              articleType={manuscript.metadata.articleType}
              journal={manuscript.metadata.journal}
              match={journalMatch}
            />
          ) : null}
          <div className="editor-tip title-page-tip">
            <Info size={15} />
            <span>
              新建稿按您的规则预置 5 位作者：第 1 位一作、第 2 位通讯作者；第 3 位起可选共一或共通讯。导入、移动和删除不会静默改写已有角色。
            </span>
          </div>
        </section>
      </main>
    );
  }

  if (activeId === "authors") {
    return (
      <AuthorsEditor
        manuscript={manuscript}
        onAddAffiliation={onAddAffiliation}
        onAddAuthor={onAddAuthor}
        onAffiliationChange={onAffiliationChange}
        onAuthorChange={onAuthorChange}
        onCoCorrespondingChange={onCoCorrespondingChange}
        onCoFirstChange={onCoFirstChange}
        onCorrespondenceNoteChange={(value) =>
          onMetadataChange("correspondence", value)
        }
        onDeleteAffiliation={onDeleteAffiliation}
        onDeleteAuthor={onDeleteAuthor}
        onMoveAuthor={onMoveAuthor}
      />
    );
  }

  if (activeId === "abstract") {
    return (
      <main className="editor-panel">
        <div className="editor-hero">
          <div className="hero-icon">
            <Type size={22} />
          </div>
          <div>
            <span className="eyebrow">FRONT MATTER</span>
            <h1>摘要与关键词</h1>
            <p>结构化摘要标签会在 Word 中单独加粗，标签后的正文保持常规字重。</p>
          </div>
        </div>

        <section className="editor-card tall">
          <div className="segmented-control" role="group" aria-label="摘要类型">
            <button
              className={manuscript.abstract.mode === "unstructured" ? "active" : ""}
              onClick={() => onAbstractChange("mode", "unstructured")}
              type="button"
            >
              非结构式摘要
            </button>
            <button
              className={manuscript.abstract.mode === "structured" ? "active" : ""}
              onClick={() => onAbstractChange("mode", "structured")}
              type="button"
            >
              结构式摘要
            </button>
          </div>

          {manuscript.abstract.mode === "structured" ? (
            <>
              <div className="structured-abstract-note">
                四个部分分别填写；Word 中只加粗 Background、Materials and Methods、Results、Conclusion 标签，标签后的内容保持常规字重。
              </div>
              <div className="structured-abstract-grid">
                {STRUCTURED_ABSTRACT_FIELDS.map(({ key, label, zhLabel }) => (
                  <label key={key}>
                    <FieldLabel>
                      {zhLabel} <small>{label}</small>
                    </FieldLabel>
                    <textarea
                      aria-label={`结构式摘要 ${zhLabel}`}
                      onChange={(event) =>
                        onStructuredAbstractChange(key, event.target.value)
                      }
                      placeholder={`填写 ${label} 内容；不要重复输入标签`}
                      value={manuscript.abstract.structured[key]}
                    />
                  </label>
                ))}
              </div>
              {manuscript.abstract.content.trim() &&
              !STRUCTURED_ABSTRACT_FIELDS.some(({ key }) =>
                manuscript.abstract.structured[key].trim(),
              ) ? (
                <label className="legacy-abstract-field">
                  <FieldLabel>未拆分的旧摘要原文</FieldLabel>
                  <textarea
                    onChange={(event) =>
                      onAbstractChange("content", event.target.value)
                    }
                    value={manuscript.abstract.content}
                  />
                  <small>原文不会丢失；请人工拆分到上面四个输入框。</small>
                </label>
              ) : null}
              <span className="word-meter structured-word-meter">
                {countWords(structuredAbstractToText(manuscript.abstract))} words
              </span>
            </>
          ) : (
            <label>
              <FieldLabel>Abstract</FieldLabel>
              <textarea
                className="body-editor abstract-editor"
                onChange={(event) =>
                  onAbstractChange("content", event.target.value)
                }
                placeholder="Paste the abstract here."
                value={manuscript.abstract.content}
              />
              <span className="word-meter">
                {countWords(manuscript.abstract.content)} words
              </span>
            </label>
          )}

          <label>
            <FieldLabel>Keywords</FieldLabel>
            <textarea
              onChange={(event) =>
                onAbstractChange("keywords", event.target.value)
              }
              placeholder="Separate keywords with semicolons"
              rows={3}
              value={manuscript.keywords}
            />
          </label>
        </section>
      </main>
    );
  }

  if (activeId === "references") {
    return (
      <ReferencesEditor
        numbering={template.references.numbering}
        onAdd={onAddReference}
        onBulkAdd={onBulkAddReferences}
        onChange={onReferenceChange}
        onDelete={onDeleteReference}
        onMove={onMoveReference}
        references={manuscript.references}
      />
    );
  }

  if (activeId === "tables") {
    return (
      <TablesEditor
        onAdd={onAddTable}
        onChange={onTableChange}
        onDelete={onDeleteTable}
        onMove={onMoveTable}
        tables={manuscript.tables}
      />
    );
  }

  if (activeId === "figures") {
    return (
      <FiguresEditor
        figures={manuscript.figures}
        onAdd={onAddFigure}
        onChange={onFigureChange}
        onDelete={onDeleteFigure}
        onMove={onMoveFigure}
      />
    );
  }

  if (activeId === "format") {
    return (
      <FormatRequirementsEditor
        onChange={onTemplateChange}
        onOpenFullDesigner={onOpenTemplateDesigner}
        template={template}
      />
    );
  }

  if (!section) return null;

  return (
    <main className="editor-panel">
      <div className="editor-hero section-hero">
        <div className="hero-icon">
          <Layers3 size={22} />
        </div>
        <div className="section-heading-copy">
          <span className="eyebrow">CONTENT BLOCK</span>
          <h1>{section.title || "未命名区块"}</h1>
          <p>
            该区块使用 H{section.level} 样式
            {section.parentId ? "，并归属于上一级标题" : "，是一级章节"}。
          </p>
        </div>
        <span className="word-count-large">{countWords(section.content)} words</span>
      </div>

      <section className="editor-card tall section-editor-card">
        <div className="section-toolbar">
          <label className="section-title-field">
            <FieldLabel>区块标题</FieldLabel>
            <input
              onChange={(event) =>
                onSectionChange(section.id, { title: event.target.value })
              }
              value={section.title}
            />
          </label>
          <div className="section-hierarchy-control">
            <FieldLabel>层级关系</FieldLabel>
            <span className="section-level-fixed">H{section.level}</span>
            {section.level < 3 ? (
              <button
                onClick={() => onAddChildSection(section.id)}
                type="button"
              >
                添加{section.level === 1 ? "二级" : "三级"}标题
              </button>
            ) : null}
          </div>
        </div>

        <label className="body-editor-label">
          <FieldLabel>
            {section.role === "declarations" ? "投稿声明内容" : "正文内容"}
          </FieldLabel>
          <textarea
            autoFocus
            className="body-editor"
            onChange={(event) =>
              onSectionChange(section.id, { content: event.target.value })
            }
            placeholder={
              section.role === "declarations"
                ? "请按真实情况填写；没有相关事项时，也应使用目标期刊允许的明确表述，不能虚构基金、伦理或利益冲突信息。"
                : section.level === 1
                ? "可直接粘贴本一级标题下的正文；也可以留空，只保留下级标题。"
                : "在这里复制粘贴该小标题下的正文；空行会保留为段落分隔。"
            }
            value={section.content}
          />
        </label>

        <div className="editor-tip">
          <Info size={15} />
          <span>
            {section.role === "declarations"
              ? "Funding、Conflict of interest、Data availability 是通用投稿前检查项；具体名称和是否必需仍以目标期刊官网为准。"
              : "正文可以留空；留空时预览和 Word 不会生成占位段落。标题层级由父子关系固定，避免 H2/H3 跑到错误章节。"}
          </span>
        </div>
      </section>
    </main>
  );
}
